import { Tab, TabList, TabProvider } from "@replicate/ui";
import { useMemo, useState } from "react";
import { match } from "ts-pattern";
import type { CogOutputSchema, CogOutputValue } from "../../types";
import { isImageUrl } from "./util";

type PixelImageSize = "1x" | "fit";

export function PixelateImagesOutput({
  output,
  schema,
}: {
  output: CogOutputValue | null;
  schema: CogOutputSchema;
}) {
  const [size, setSize] = useState<PixelImageSize>("fit");

  const imgClass = useMemo(() => {
    if (size === "1x") {
      return "max-w-full object-contain";
    }
    if (size === "fit") {
      return "w-full h-full";
    }
  }, [size]);

  const tabs = (
    <div className="flex items-center gap-2 mb-4">
      <TabProvider
        setSelectedId={(val) => setSize(val as PixelImageSize)}
        selectedId={size}
      >
        <TabList className="!text-r8-xs" size="sm" variant="pills">
          <Tab value="1x" id="1x">
            1x
          </Tab>
          <Tab value="fit" id="fit">
            Fit
          </Tab>
        </TabList>
      </TabProvider>
    </div>
  );

  if (output == null) {
    return null;
  }

  return match(schema)
    .with({ type: "string", format: "uri" }, () => {
      if (typeof output === "string" && isImageUrl(output)) {
        return (
          <div>
            {tabs}
            <PixelatedImage url={output} className={imgClass} />
          </div>
        );
      }
      return null;
    })
    .with({ type: "array", items: { type: "string", format: "uri" } }, () => {
      if (Array.isArray(output)) {
        return (
          <div>
            <div>{tabs}</div>
            <div className="flex flex-col space-y-2">
              {output.map((url, index) => {
                if (typeof url === "string" && isImageUrl(url)) {
                  return (
                    <PixelatedImage
                      key={index}
                      url={url}
                      className={imgClass}
                    />
                  );
                }
                return null;
              })}
            </div>
          </div>
        );
      }
      return null;
    })
    .otherwise(() => null);
}

function PixelatedImage({
  url,
  className = "",
}: { url: string; className?: string }) {
  if (url.startsWith("data:")) {
    return <code>(not stored)</code>;
  }

  return (
    <div>
      <a href={url} rel="noreferrer" target="_blank" className="block">
        <img
          src={url}
          alt=""
          className={className}
          style={{ imageRendering: "pixelated" }}
        />
      </a>
    </div>
  );
}

export default PixelateImagesOutput;
